import { useInvitationEmailAttachments } from '@/hooks/use-invitation-email-attachments';
import { useAuth } from '@/contexts/auth-context';
import { useTemplateLibraryQuery } from '@/hooks/queries/use-template-library-query';
import { VStack } from '@chakra-ui/react';
import {
  TemplateChannel,
  TemplateContext,
  TemplateType,
  VARIABLE_REGISTRY,
  type CollabProposalEmailInput,
  type TemplatePair,
} from '@fluenceur/types';
import type { AvailableVariable } from '@fluenceur/ui';
import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import {
  OutreachEmailComposer,
  type OutreachEmailState,
} from '../outreach/outreach-email-composer';
import { useOutreachComposerAssembly } from '../outreach/use-outreach-composer-assembly';
import { useOutreachLanguage } from '../outreach/use-outreach-language';
import { pickCollabEmailTemplate, templateEditorContent } from './collab-proposal-email-template';

/** The (channel, context) pair backing the sponsored proposal email library. */
const COLLAB_EMAIL_PAIR: TemplatePair = {
  channel: TemplateChannel.EMAIL,
  context: TemplateContext.COLLAB,
};

/** What the module reports to the compose step on every edit. */
export interface CollabProposalEmailState {
  /** Ready-to-send payload, or null while the body is empty (or still loading). */
  proposalEmail: CollabProposalEmailInput | null;
  /** False when a required variable ({{collab_link}}) is missing from the body. */
  isValid: boolean;
}

interface CollabProposalEmailModuleProps {
  /** Called on every editor change; the compose step owns the send. */
  onStateChange: (state: CollabProposalEmailState) => void;
}

/**
 * Editable proposal email of the wizard's compose step (email mode). A thin
 * caller of the shared OutreachEmailComposer that injects the (EMAIL, COLLAB)
 * library template + the `{{collab_link}}` requirement, and maps the composer's
 * state to the compose step's payload. The whole template wiring (library
 * selector + save-as-template, seed guard, body/key resolution, subject row)
 * comes from the shared composer assembly; the step owns the surrounding chrome
 * (banner, notes, CTA) and the editor's own header carries the "Message" label
 * and the reset action.
 *
 * Prefill precedence mirrors the affiliation step: a selected library entry >
 * manual edits > the system-default library template (there is no program-scoped
 * prepare endpoint for a sponsored collab). Variables come from
 * VARIABLE_REGISTRY: chips serialize back to `{{key}}` mustache text and are
 * NEVER resolved client-side ({{collab_link}} only exists once the lookup key is
 * minted server-side). Mono and bulk share the exact same surface: bulk ships
 * one common message the server personalizes per recipient. The subject rides
 * its own row above the composer: seeded from the copy localized in the BODY
 * language, editable, and shipped in `proposalEmail` with its chips left as
 * mustache text for the send rail to resolve per recipient.
 */
export const CollabProposalEmailModule: FC<CollabProposalEmailModuleProps> = ({
  onStateChange,
}) => {
  const { user } = useAuth();
  const files = useInvitationEmailAttachments();
  const { selectedLanguage } = useOutreachLanguage();
  const brandId = user?.currentBrand?.id ?? '';

  // System-default library entry as the base body (no program-scoped prepare
  // endpoint for a sponsored collab). A selected brand template overrides it
  // through the assembly's store snapshot.
  const { data: entries, isLoading } = useTemplateLibraryQuery(brandId, {
    channel: TemplateChannel.EMAIL,
    context: TemplateContext.COLLAB,
  });
  const fallbackContent = templateEditorContent(pickCollabEmailTemplate(entries, selectedLanguage));

  const availableVariables: AvailableVariable[] = useMemo(
    () =>
      VARIABLE_REGISTRY[TemplateType.COLLAB_PROPOSAL_EMAIL].map((variable) => ({
        key: variable.key,
        label: variable.label,
        labelKey: variable.labelKey,
        description: variable.description,
        descriptionKey: variable.descriptionKey,
        required: variable.required,
        // `{{auto}}` marks a server-resolved chip; nothing resolves client-side.
        value: '{{auto}}',
      })),
    []
  );

  // Shared composer assembly: template library flow (selector + save), seed
  // guard, body/key resolution and the editable subject row.
  const {
    composerKey,
    editorContent,
    editorFooter,
    saveAsTemplatePanel,
    subjectRow,
    subject,
    isSubjectValid,
    registerEmission,
  } = useOutreachComposerAssembly({
    attachmentControls: files,
    brandId: brandId || undefined,
    pair: COLLAB_EMAIL_PAIR,
    fallbackContent,
    availableVariables,
    controlsDisabled: isLoading,
  });

  const [composerState, setComposerState] = useState<OutreachEmailState | null>(null);

  const handleChange = useCallback(
    (state: OutreachEmailState) => {
      setComposerState(state);
      // Mirror the live body into the store (shared guard: the first emission
      // after a mount only becomes the "original" when the store was clean).
      registerEmission(state.messageSnapshot);
    },
    [registerEmission]
  );

  // Report the send payload upward whenever either side changes: the subject
  // lives outside the composer, so an edit to it alone must still reach the
  // step. Silent until the composer's first emission, which leaves the step's
  // initial state untouched while the template loads.
  useEffect(() => {
    if (!composerState) return;
    onStateChange({
      // Subject in the body language, compiled HTML with chips kept as `{{key}}`
      // mustache text.
      proposalEmail: composerState.bodyHtml
        ? {
            subject,
            body: composerState.bodyHtml,
            ...(files.attachments.length
              ? { attachmentIds: files.attachments.map((file) => file.id) }
              : {}),
          }
        : null,
      isValid: composerState.isValid && isSubjectValid && !files.isBlocked,
    });
  }, [composerState, subject, isSubjectValid, onStateChange, files.attachments, files.isBlocked]);

  return (
    <VStack align="stretch" gap="3">
      {subjectRow}

      <OutreachEmailComposer
        key={composerKey}
        templateSource={{ initialContent: editorContent, availableVariables, isLoading }}
        requiredLinkVariable="{{collab_link}}"
        onChange={handleChange}
        editorFooter={editorFooter}
        attachmentControl={{
          attachments: files.attachments,
          onFileSelect: files.handleFileSelect,
          onRemoveAttachment: files.handleRemoveAttachment,
          isUploading: files.isUploading,
          deletingId: files.deletingId,
          issues: files.issues,
          onDismissIssue: files.dismissIssue,
          validationError: files.validationError,
          maxTotalBytes: files.maxTotalBytes,
        }}
      />

      {saveAsTemplatePanel}
    </VStack>
  );
};
